// /corporate-accounts/:id — Corporate Account Detail (PAGES.md §3.102)
//
// View/edit corporate account. Shows company info, credit, orders, invoices.
// Create bulk orders, download PDF invoices, manage credit limits.
// SHARED vertical. Min role: MANAGER (30) view/edit, OWNER (50) deactivate.
// Traceability: FR-CORP-001

import { getSubunitMultiplier } from '@bbpos/utils';
import { useState, useCallback, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  PageContainer,
  Button,
  Badge,
  Input,
  Select,
  TextArea,
  DatePicker,
  Skeleton,
  EmptyState,
  ErrorBoundary,
  StatCard,
  DataTable,
  Tabs,
  Alert,
  Modal,
  RadioGroup,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import { formatPaise } from '@/utils/currency';
import { ROLE_LEVELS } from '@/types/user';
import { useAuthStore } from '@/stores/use-auth-store';
import { useDashboardStore } from '@/stores/use-dashboard-store';
import { useTenantCurrency, getCurrencySymbol } from '@/hooks/use-tenant-currency';
import { useTenantDial } from '@/hooks/use-tenant-dial';
import { useTaxRegime } from '@/hooks/use-tax-regime';
import { corporateAccountService } from '@/services/corporate-account.service';
import { productService } from '@/services/product.service';
import { locationService } from '@/services/location.service';
import { managerApprovalService } from '@/services/manager-approval.service';
import type { Approver } from '@/services/manager-approval.service';
import type {
  CorporateAccount,
  CorporateOrder,
  CorporateOrderAction,
  CorporateInvoice,
  CorporatePayment,
  CorporatePaymentMethod,
  CreateCorporateOrderDto,
} from '@/services/corporate-account.service';
import { ApiError } from '@/lib/api-client';
import { GLASS, GLASS_FILTER, cn } from '@/constants/design-tokens';

// ─── Constants ─────────────────────────────────────────────────────────────

const ROLE_LEVEL_OWNER = ROLE_LEVELS.OWNER;
const ROLE_LEVEL_MANAGER = ROLE_LEVELS.MANAGER;

type TabId = 'orders' | 'invoices' | 'payments' | 'details';
const TABS: { id: TabId; label: string }[] = [
  { id: 'orders', label: 'Orders' },
  { id: 'invoices', label: 'Invoices' },
  { id: 'payments', label: 'Payments' },
  { id: 'details', label: 'Details' },
];

// The API's real statuses (QA CU3-002) — PLACED was shown as "Pending" by the fallback.
const ORDER_STATUS_BADGE: Record<string, { label: string; variant: 'success' | 'warning' | 'danger' | 'draft' | 'pending' | 'info' | 'parked' }> = {
  PLACED: { label: 'Placed', variant: 'pending' },
  CONFIRMED: { label: 'Confirmed', variant: 'info' },
  DELIVERED: { label: 'Delivered', variant: 'parked' },
  INVOICED: { label: 'Invoiced', variant: 'success' },
  CANCELLED: { label: 'Cancelled', variant: 'danger' },
};

const INVOICE_STATUS_BADGE: Record<string, { label: string; variant: 'success' | 'warning' | 'pending' }> = {
  UNPAID: { label: 'Unpaid', variant: 'warning' },
  PARTIALLY_PAID: { label: 'Part paid', variant: 'pending' },
  PAID: { label: 'Paid', variant: 'success' },
};

// Next steps per status (owner decision C8): Confirm → Deliver → Invoice; Cancel before Deliver.
const ORDER_ACTIONS: Record<string, CorporateOrderAction[]> = {
  PLACED: ['confirm', 'cancel'],
  CONFIRMED: ['deliver', 'cancel'],
  DELIVERED: ['invoice'],
};

const ACTION_COPY: Record<CorporateOrderAction, { label: string; title: string; body: string; done: string }> = {
  confirm: { label: 'Confirm', title: 'Confirm this order?', body: 'The order is accepted and can be delivered next.', done: 'Order confirmed' },
  deliver: { label: 'Deliver', title: 'Deliver this order?', body: 'The items leave stock at the order\'s store now.', done: 'Order delivered — stock updated' },
  invoice: { label: 'Invoice', title: 'Invoice this order?', body: 'An invoice is raised and its total is added to the account\'s outstanding.', done: 'Invoice raised' },
  cancel: { label: 'Cancel', title: 'Cancel this order?', body: 'The order is cancelled. This cannot be undone.', done: 'Order cancelled' },
};

const PAYMENT_METHOD_OPTIONS: Array<{ value: CorporatePaymentMethod; label: string }> = [
  { value: 'BANK_TRANSFER', label: 'Bank transfer' },
  { value: 'UPI', label: 'UPI' },
  { value: 'CASH', label: 'Cash' },
  { value: 'CARD', label: 'Card' },
];

const PHONE_REGEX = /^\+[1-9]\d{6,14}$/;

// ─── Icons ─────────────────────────────────────────────────────────────────

function DownloadIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
    </svg>
  );
}

function BuildingIcon({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 21h18M3 10h18M5 6l7-3 7 3M4 10v11M20 10v11M8 14v.01M12 14v.01M16 14v.01M8 18v.01M12 18v.01M16 18v.01" />
    </svg>
  );
}

function CurrencyIcon({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="6" y1="3" x2="6" y2="15" /><path d="M6 3h8a4 4 0 010 8H6" /><path d="M14 11l4 10" /><line x1="4" y1="3" x2="12" y2="3" /><line x1="4" y1="7" x2="16" y2="7" />
    </svg>
  );
}

function PackageIcon({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="16.5" y1="9.4" x2="7.5" y2="4.21" />
      <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
      <polyline points="3.27 6.96 12 12.01 20.73 6.96" /><line x1="12" y1="22.08" x2="12" y2="12" />
    </svg>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return iso; }
}

// Format a Date to YYYY-MM-DD using LOCAL parts. Using toISOString() here would
// shift the date back a day for UTC+ timezones (e.g. IST), so a clicked 11th
// would be stored as the 10th.
function toLocalIsoDate(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Component ──────────────────────────────────────────────────────────────

function CorporateAccountDetailContent() {
  const dial = useTenantDial();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const currency = useTenantCurrency();
  // Statutory tax-id vocabulary/validation derives from the immutable currency.
  // INR → GST_INDIA: label `GSTIN`, GSTIN regex (byte-identical to before).
  // GCC → `TRN`/`VATIN`/… + 15-digit regex; Qatar/Kuwait have no statutory id
  // (`taxIdLabel === null`) ⇒ the field is hidden entirely.
  const tax = useTaxRegime();
  const selectedLocationId = useDashboardStore((s) => s.selectedLocationId);

  // Role
  const profile = useAuthStore((s) => s.profile);
  const roleLevel = ROLE_LEVELS[profile?.role ?? ''] ?? 0;
  const canDeactivate = roleLevel >= ROLE_LEVEL_OWNER;
  const canCreateOrder = roleLevel >= ROLE_LEVEL_MANAGER;
  // An owner places an over-limit order directly; anyone else needs an owner's approval (CU3-001).
  const isOwner = roleLevel >= ROLE_LEVEL_OWNER;
  // PATCH /corporate-accounts/:id is OWNER-only; a manager sees the details read-only (CU3-003).
  const canEdit = roleLevel >= ROLE_LEVEL_OWNER;

  // Data
  const [account, setAccount] = useState<CorporateAccount | null>(null);
  const [orders, setOrders] = useState<CorporateOrder[]>([]);
  const [invoices, setInvoices] = useState<CorporateInvoice[]>([]);
  const [payments, setPayments] = useState<CorporatePayment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>('orders');

  // Edit state
  const [editCompanyName, setEditCompanyName] = useState('');
  const [editGstin, setEditGstin] = useState('');
  const [editContactName, setEditContactName] = useState('');
  const [editContactPhone, setEditContactPhone] = useState('');
  const [editContactEmail, setEditContactEmail] = useState('');
  const [editCreditLimitRupees, setEditCreditLimitRupees] = useState('');
  const [editBillingAddress, setEditBillingAddress] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Order modal
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [orderItems, setOrderItems] = useState<Array<{ product_id: string; product_name: string; quantity: number; unit_price: number }>>([]);
  const [orderDeliveryDate, setOrderDeliveryDate] = useState('');
  const [orderNotes, setOrderNotes] = useState('');
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  // Over the credit limit: the API's refusal text, and the owner approving with their PIN.
  const [approvalMessage, setApprovalMessage] = useState<string | null>(null);
  const [approvers, setApprovers] = useState<Approver[]>([]);
  const [approverId, setApproverId] = useState('');
  const [approverPin, setApproverPin] = useState('');

  // Order lifecycle step waiting for the in-app confirm (no native confirm()).
  const [pendingAction, setPendingAction] = useState<{ order: CorporateOrder; action: CorporateOrderAction } | null>(null);
  const [isActing, setIsActing] = useState(false);

  // Record payment modal
  const [paymentInvoice, setPaymentInvoice] = useState<CorporateInvoice | null>(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<CorporatePaymentMethod>('BANK_TRANSFER');
  const [paymentReference, setPaymentReference] = useState('');
  const [isRecordingPayment, setIsRecordingPayment] = useState(false);

  // Product picker options for the order modal.
  const [productOptions, setProductOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [productMap, setProductMap] = useState<Record<string, { name: string; price: number; gst_rate: number }>>({});

  // Location picker for the order modal. Corporate orders are location-scoped
  // (products live per location), so a concrete location must be chosen — the
  // global "All Locations" filter (empty id) is not a valid order target.
  const [locationOptions, setLocationOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [orderLocationId, setOrderLocationId] = useState('');

  // Load active locations; default the order location to the global filter, or
  // the first location when "All Locations" is selected.
  useEffect(() => {
    let cancelled = false;
    locationService
      .list({ limit: 100, is_active: 'true' })
      .then((res) => {
        if (cancelled) return;
        const opts = res.data.map((l) => ({ value: l.id, label: l.name }));
        setLocationOptions(opts);
        setOrderLocationId((cur) => cur || selectedLocationId || opts[0]?.value || '');
      })
      .catch(() => { /* silent — selector simply stays empty */ });
    return () => { cancelled = true; };
  }, [selectedLocationId]);

  // Products are location-scoped — (re)load them whenever the order location changes.
  useEffect(() => {
    if (!orderLocationId) { setProductOptions([]); setProductMap({}); return; }
    let cancelled = false;
    productService
      .list({ limit: 200, is_active: 'true', sort: 'name', order: 'asc', location_id: orderLocationId })
      .then((res) => {
        if (cancelled) return;
        const map: Record<string, { name: string; price: number; gst_rate: number }> = {};
        setProductOptions(
          res.data.map((p) => {
            const price = (p as unknown as { price?: number }).price ?? 0;
            map[p.id] = { name: p.name, price, gst_rate: Number(p.gst_rate ?? 0) };
            return { value: p.id, label: p.sku ? `${p.name} (${p.sku})` : p.name };
          }),
        );
        setProductMap(map);
      })
      .catch(() => { /* silent — order can still be created by typing */ });
    return () => { cancelled = true; };
  }, [orderLocationId]);

  useEffect(() => { document.title = account ? `${account.company_name} — Corporate Accounts — Orio360 Admin` : 'Corporate Account — Orio360 Admin'; }, [account]);

  // Fetch account
  const fetchAccount = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    setLoadError(null);
    try {
      const data = await corporateAccountService.get(id);
      setAccount(data);
      setEditCompanyName(data.company_name);
      setEditGstin(data.gstin ?? '');
      setEditContactName(data.contact_name);
      setEditContactPhone(data.contact_phone);
      setEditContactEmail(data.contact_email ?? '');
      setEditCreditLimitRupees(String(data.credit_limit / getSubunitMultiplier(currency)));
      setEditBillingAddress(data.billing_address ?? '');
    } catch (err) {
      if (err instanceof ApiError && err.statusCode === 404) {
        toast({ title: 'Account not found', variant: 'error' });
        navigate('/corporate-accounts', { replace: true });
      } else {
        const message = err instanceof ApiError ? err.message : 'Failed to load account.';
        setLoadError(message);
        toast({ title: message, variant: 'error' });
      }
    } finally {
      setIsLoading(false);
    }
  }, [id, navigate, toast]);

  useEffect(() => { fetchAccount(); }, [fetchAccount]);

  // Fetch orders
  useEffect(() => {
    if (!id || activeTab !== 'orders') return;
    corporateAccountService.listOrders(id).then((r) => setOrders(r.data)).catch(() => toast({ title: 'Failed to load orders', variant: 'error' }));
  }, [id, activeTab, toast]);

  // Fetch invoices
  const fetchInvoices = useCallback(() => {
    if (!id) return;
    corporateAccountService.listInvoices(id).then(setInvoices).catch(() => toast({ title: 'Failed to load invoices', variant: 'error' }));
  }, [id, toast]);

  useEffect(() => {
    if (activeTab === 'invoices') fetchInvoices();
  }, [activeTab, fetchInvoices]);

  // Fetch payments
  useEffect(() => {
    if (!id || activeTab !== 'payments') return;
    corporateAccountService.listPayments(id).then(setPayments).catch(() => toast({ title: 'Failed to load payments', variant: 'error' }));
  }, [id, activeTab, toast]);

  // Computed — the API's figure counts open (not yet invoiced) orders too, like the limit check.
  const availableCredit = useMemo(() => {
    if (!account) return 0;
    return Math.max(0, account.available_credit ?? account.credit_limit - account.outstanding_balance);
  }, [account]);

  // The limit counts the total incl. tax (owner decision C7). Same per-line rounding as
  // the API: India GST floors, GCC VAT rounds half-up in minor units.
  const orderTotal = useMemo(() =>
    orderItems.reduce((sum, item) => {
      const line = item.unit_price * item.quantity;
      const rawTax = (line * (productMap[item.product_id]?.gst_rate ?? 0)) / 100;
      return sum + line + (tax.taxName === 'GST' ? Math.floor(rawTax) : Math.round(rawTax));
    }, 0),
  [orderItems, productMap, tax.taxName]);

  const creditExceeded = useMemo(() => {
    if (!account) return false;
    return orderTotal > availableCredit;
  }, [account, orderTotal, availableCredit]);

  // The API has the last word (it also counts open orders), so its 409 turns this on too.
  const needsOwnerApproval = !isOwner && (creditExceeded || approvalMessage !== null);

  // Owners who can approve (they need a POS PIN). Owners are not tied to a store.
  useEffect(() => {
    if (!orderModalOpen || !needsOwnerApproval) return;
    let cancelled = false;
    managerApprovalService
      .listApprovers(orderLocationId || undefined)
      .then((list) => { if (!cancelled) setApprovers(list.filter((a) => a.role === 'OWNER')); })
      .catch(() => { /* silent — the panel says no owner has a PIN */ });
    return () => { cancelled = true; };
  }, [orderModalOpen, needsOwnerApproval, orderLocationId]);

  const closeOrderModal = useCallback(() => {
    setOrderModalOpen(false);
    setApprovalMessage(null);
    setApproverPin('');
  }, []);

  // Save details
  const handleSave = useCallback(async () => {
    if (!id || !canEdit) return;
    if (editGstin && tax.taxIdRegex && !tax.taxIdRegex.test(editGstin.toUpperCase())) {
      toast({ title: `Invalid ${tax.taxIdLabel ?? 'tax ID'} format`, variant: 'error' }); return;
    }
    if (!PHONE_REGEX.test(editContactPhone)) {
      toast({ title: 'Invalid phone number', variant: 'error' }); return;
    }
    if (editCreditLimitRupees.trim() === '') {
      toast({ title: 'Credit limit is required (enter 0 for no credit)', variant: 'error' }); return;
    }
    const creditLimitPaise = Math.round(Number(editCreditLimitRupees) * getSubunitMultiplier(currency));
    if (!Number.isFinite(creditLimitPaise) || creditLimitPaise < 0) {
      toast({ title: 'Invalid credit limit', variant: 'error' }); return;
    }
    setIsSaving(true);
    try {
      const updated = await corporateAccountService.update(id, {
        company_name: editCompanyName.trim(),
        gstin: editGstin.toUpperCase().trim() || undefined,
        contact_name: editContactName.trim(),
        contact_phone: editContactPhone.trim(),
        contact_email: editContactEmail.trim() || undefined,
        credit_limit: creditLimitPaise,
        billing_address: editBillingAddress.trim() || undefined,
      });
      // Merge, so a figure the response leaves out never turns a tile into NaN (CU3-002).
      setAccount((prev) => (prev ? { ...prev, ...updated } : updated));
      toast({ title: 'Corporate account updated', variant: 'success' });
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to save', variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  }, [id, canEdit, editCompanyName, editGstin, editContactName, editContactPhone, editContactEmail, editCreditLimitRupees, editBillingAddress, tax, toast]);

  // Deactivate
  const handleDeactivate = useCallback(async () => {
    if (!id || !canDeactivate) return;
    if (!window.confirm('Are you sure you want to deactivate this corporate account? This action can be reversed.')) return;
    try {
      const updated = await corporateAccountService.deactivate(id);
      setAccount(updated);
      toast({ title: 'Account deactivated', variant: 'success' });
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to deactivate', variant: 'error' });
    }
  }, [id, canDeactivate, toast]);

  // Create order
  const handleCreateOrder = useCallback(async () => {
    if (!id || !canCreateOrder) return;
    if (!orderLocationId) { toast({ title: 'Select a location for this order', variant: 'error' }); return; }
    const validItems = orderItems.filter((i) => i.product_id && i.quantity > 0);
    if (validItems.length === 0) { toast({ title: 'Add at least one item', variant: 'error' }); return; }

    setIsCreatingOrder(true);
    try {
      let approvalToken: string | undefined;
      if (needsOwnerApproval) {
        try {
          const grant = await managerApprovalService.verify({
            approver_id: approverId,
            pin: approverPin,
            action: 'CORPORATE_OVER_LIMIT',
            subject_id: id,
            location_id: orderLocationId,
          });
          approvalToken = grant.approval_token;
        } catch (err) {
          setApproverPin('');
          throw err;
        }
      }
      const dto: CreateCorporateOrderDto = {
        corporate_account_id: id,
        location_id: orderLocationId,
        items: validItems.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        delivery_date: orderDeliveryDate || undefined,
        notes: orderNotes.trim() || undefined,
        ...(approvalToken ? { approval_token: approvalToken } : {}),
      };
      await corporateAccountService.createOrder(dto);
      toast({ title: `Corporate order created for ${account?.company_name}`, variant: 'success' });
      closeOrderModal();
      setOrderItems([]);
      setOrderDeliveryDate('');
      setOrderNotes('');
      fetchAccount();
      // Refresh orders tab
      if (activeTab === 'orders') {
        corporateAccountService.listOrders(id).then((r) => setOrders(r.data)).catch(() => toast({ title: 'Failed to refresh orders', variant: 'error' }));
      }
    } catch (err) {
      if (err instanceof ApiError && err.code === 'APPROVAL_REQUIRED') {
        // Over the limit on the server's figures — ask for an owner's approval.
        setApprovalMessage(err.message);
      } else {
        toast({ title: err instanceof ApiError ? err.message : 'Failed to create order', variant: 'error' });
      }
    } finally {
      setIsCreatingOrder(false);
    }
  }, [id, canCreateOrder, orderItems, orderDeliveryDate, orderNotes, orderLocationId, account, needsOwnerApproval, approverId, approverPin, closeOrderModal, activeTab, fetchAccount, toast]);

  // Download invoice
  const handleDownloadInvoice = useCallback(async (orderId: string, invoiceNumber: string) => {
    try {
      const blob = await corporateAccountService.downloadInvoice(orderId);
      downloadBlob(blob, `${invoiceNumber}.pdf`);
      toast({ title: 'Invoice downloaded', variant: 'success' });
    } catch {
      toast({ title: 'Failed to download invoice', variant: 'error' });
    }
  }, [toast]);

  // Confirm / Deliver / Invoice / Cancel an order (QA CU3-002)
  const handleOrderAction = useCallback(async () => {
    if (!id || !pendingAction) return;
    const { order, action } = pendingAction;
    setIsActing(true);
    try {
      await corporateAccountService.transitionOrder(order.id, action);
      toast({ title: ACTION_COPY[action].done, variant: 'success' });
      setPendingAction(null);
      fetchAccount();
      corporateAccountService.listOrders(id).then((r) => setOrders(r.data)).catch(() => toast({ title: 'Failed to refresh orders', variant: 'error' }));
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to update the order', variant: 'error' });
    } finally {
      setIsActing(false);
    }
  }, [id, pendingAction, fetchAccount, toast]);

  const openPaymentModal = useCallback((invoice: CorporateInvoice) => {
    setPaymentInvoice(invoice);
    setPaymentAmount(String(invoice.balance / getSubunitMultiplier(currency)));
    setPaymentMethod('BANK_TRANSFER');
    setPaymentReference('');
  }, [currency]);

  // Record a payment against an invoice — lowers the outstanding (QA CU3-002)
  const handleRecordPayment = useCallback(async () => {
    if (!id || !paymentInvoice) return;
    const amountMinor = Math.round(Number(paymentAmount) * getSubunitMultiplier(currency));
    if (!Number.isFinite(amountMinor) || amountMinor <= 0) {
      toast({ title: 'Enter an amount above zero', variant: 'error' }); return;
    }
    if (amountMinor > paymentInvoice.balance) {
      toast({ title: `Only ${formatPaise(paymentInvoice.balance, currency)} is left to pay on this invoice`, variant: 'error' }); return;
    }
    setIsRecordingPayment(true);
    try {
      await corporateAccountService.recordPayment(id, {
        order_id: paymentInvoice.order_id,
        amount: amountMinor,
        method: paymentMethod,
        reference: paymentReference.trim() || undefined,
      });
      toast({ title: `Payment of ${formatPaise(amountMinor, currency)} recorded`, variant: 'success' });
      setPaymentInvoice(null);
      fetchAccount();
      fetchInvoices();
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to record payment', variant: 'error' });
    } finally {
      setIsRecordingPayment(false);
    }
  }, [id, paymentInvoice, paymentAmount, paymentMethod, paymentReference, currency, fetchAccount, fetchInvoices, toast]);

  // Keyboard shortcuts
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;
      if (e.ctrlKey && e.key === 's') { e.preventDefault(); handleSave(); }
      if (e.key === 'o' && canCreateOrder && !orderModalOpen) { e.preventDefault(); setOrderModalOpen(true); }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleSave, canCreateOrder, orderModalOpen]);

  // Order columns
  const orderColumns: ColumnDef<CorporateOrder>[] = useMemo(() => [
    {
      key: 'order_number', header: 'Order #',
      render: (_v: unknown, row: CorporateOrder) => (
        <span className="text-xs font-normal font-mono tracking-wide text-neutral-500 dark:text-neutral-400">{row.order_number}</span>
      ),
    },
    {
      key: 'created_at', header: 'Date',
      render: (_v: unknown, row: CorporateOrder) => (
        <span className="text-sm font-medium font-mono tabular-nums text-neutral-700 dark:text-neutral-300">{formatDate(row.created_at)}</span>
      ),
    },
    {
      key: 'items', header: 'Items',
      render: (_v: unknown, row: CorporateOrder) => (
        <span className="text-lg font-bold font-mono tabular-nums text-neutral-900 dark:text-neutral-100">{row.item_count ?? row.items?.length ?? 0}</span>
      ),
    },
    {
      key: 'total', header: 'Amount', numeric: true,
      render: (_v: unknown, row: CorporateOrder) => (
        <span className="text-sm font-bold font-mono tabular-nums text-neutral-900 dark:text-neutral-100">{formatPaise(row.total, currency)}</span>
      ),
    },
    {
      key: 'delivery_date', header: 'Delivery',
      render: (_v: unknown, row: CorporateOrder) => (
        <span className="text-sm font-medium font-mono tabular-nums text-neutral-700 dark:text-neutral-300">{formatDate(row.delivery_date)}</span>
      ),
    },
    {
      key: 'status', header: 'Status',
      render: (_v: unknown, row: CorporateOrder) => {
        const s = ORDER_STATUS_BADGE[row.status] ?? { label: row.status, variant: 'draft' as const };
        return <Badge variant={s.variant} size="sm">{s.label}</Badge>;
      },
    },
    {
      key: 'id', header: '',
      render: (_v: unknown, row: CorporateOrder) => (
        <div className="flex items-center justify-end gap-2">
          {canCreateOrder && (ORDER_ACTIONS[row.status] ?? []).map((action) => (
            <button
              key={action}
              type="button"
              onClick={(e) => { e.stopPropagation(); setPendingAction({ order: row, action }); }}
              className={cn(
                'text-xs font-semibold px-2.5 py-1 rounded-lg transition-all duration-200',
                action === 'cancel'
                  ? 'text-danger-600 dark:text-danger-400 hover:bg-danger-50 dark:hover:bg-danger-900/30'
                  : 'text-white bg-gradient-to-r from-indigo-500 to-violet-500 hover:shadow-md',
              )}
              aria-label={`${ACTION_COPY[action].label} order ${row.order_number}`}
            >
              {ACTION_COPY[action].label}
            </button>
          ))}
          {row.status === 'INVOICED' && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); handleDownloadInvoice(row.id, row.invoice_number ?? row.order_number); }}
              className="text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-200 transition-colors duration-200 flex items-center gap-1"
              aria-label={`Download invoice for ${row.order_number}`}
            >
              <DownloadIcon /> Invoice PDF
            </button>
          )}
        </div>
      ),
    },
  ], [currency, canCreateOrder, handleDownloadInvoice]);

  // Invoice columns
  const invoiceColumns: ColumnDef<CorporateInvoice>[] = useMemo(() => [
    {
      key: 'invoice_number', header: 'Invoice #',
      render: (_v: unknown, row: CorporateInvoice) => (
        <span className="text-xs font-normal font-mono tracking-wide text-neutral-500 dark:text-neutral-400">{row.invoice_number}</span>
      ),
    },
    {
      key: 'order_number', header: 'Order #',
      render: (_v: unknown, row: CorporateInvoice) => (
        <span className="text-xs font-normal font-mono tracking-wide text-neutral-500 dark:text-neutral-400">{row.order_number}</span>
      ),
    },
    {
      key: 'created_at', header: 'Date',
      render: (_v: unknown, row: CorporateInvoice) => (
        <span className="text-sm font-mono tabular-nums text-neutral-700 dark:text-neutral-300">{formatDate(row.created_at)}</span>
      ),
    },
    {
      key: 'amount', header: 'Amount', numeric: true,
      render: (_v: unknown, row: CorporateInvoice) => (
        <span className="text-sm font-bold font-mono tabular-nums text-neutral-900 dark:text-neutral-100">{formatPaise(row.amount, currency)}</span>
      ),
    },
    {
      key: 'paid_amount', header: 'Paid', numeric: true,
      render: (_v: unknown, row: CorporateInvoice) => (
        <span className="text-sm font-mono tabular-nums text-success-600 dark:text-success-400">{formatPaise(row.paid_amount, currency)}</span>
      ),
    },
    {
      key: 'balance', header: 'Balance', numeric: true,
      render: (_v: unknown, row: CorporateInvoice) => (
        <span className="text-sm font-bold font-mono tabular-nums text-neutral-900 dark:text-neutral-100">{formatPaise(row.balance, currency)}</span>
      ),
    },
    {
      key: 'status', header: 'Status',
      render: (_v: unknown, row: CorporateInvoice) => {
        const s = INVOICE_STATUS_BADGE[row.status] ?? INVOICE_STATUS_BADGE.UNPAID;
        return <Badge variant={s.variant} size="sm">{s.label}</Badge>;
      },
    },
    {
      key: 'order_id', header: '',
      render: (_v: unknown, row: CorporateInvoice) => (
        <div className="flex items-center justify-end gap-3">
          {canCreateOrder && row.balance > 0 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); openPaymentModal(row); }}
              className="text-xs font-semibold px-2.5 py-1 rounded-lg text-white bg-gradient-to-r from-teal-500 to-success-500 hover:shadow-md transition-all duration-200"
              aria-label={`Record payment for ${row.invoice_number}`}
            >
              Record payment
            </button>
          )}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); handleDownloadInvoice(row.order_id, row.invoice_number); }}
            className="text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-200 transition-colors duration-200 flex items-center gap-1"
            aria-label={`Download invoice ${row.invoice_number}`}
          >
            <DownloadIcon /> PDF
          </button>
        </div>
      ),
    },
  ], [handleDownloadInvoice, currency, canCreateOrder, openPaymentModal]);

  const paymentColumns: ColumnDef<CorporatePayment>[] = useMemo(() => [
    {
      key: 'paid_at', header: 'Date',
      render: (_v: unknown, row: CorporatePayment) => (
        <span className="text-sm font-mono tabular-nums text-neutral-700 dark:text-neutral-300">{formatDate(row.paid_at)}</span>
      ),
    },
    {
      key: 'invoice_number', header: 'Invoice #',
      render: (_v: unknown, row: CorporatePayment) => (
        <span className="text-xs font-mono tracking-wide text-neutral-500 dark:text-neutral-400">{row.invoice_number}</span>
      ),
    },
    {
      key: 'method', header: 'Method',
      render: (_v: unknown, row: CorporatePayment) => (
        <Badge variant="info" size="sm">{PAYMENT_METHOD_OPTIONS.find((m) => m.value === row.method)?.label ?? row.method}</Badge>
      ),
    },
    {
      key: 'reference', header: 'Reference',
      render: (_v: unknown, row: CorporatePayment) => (
        <span className="text-sm text-neutral-700 dark:text-neutral-300">{row.reference ?? '—'}</span>
      ),
    },
    {
      key: 'amount', header: 'Amount', numeric: true,
      render: (_v: unknown, row: CorporatePayment) => (
        <span className="text-sm font-bold font-mono tabular-nums text-success-600 dark:text-success-400">{formatPaise(row.amount, currency)}</span>
      ),
    },
  ], [currency]);

  if (isLoading) {
    return (
      <PageContainer
        title="Corporate Account"
        breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Corporate Accounts', href: '/corporate-accounts' }, { label: 'Loading...' }]}
      >
        <div className="space-y-6 animate-fade-in">
          <Skeleton variant="rect" height={120} className="rounded-2xl" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => <StatCard key={i} title="" value="" loading />)}
          </div>
          <Skeleton variant="rect" height={300} className="rounded-2xl" />
        </div>
      </PageContainer>
    );
  }

  if (!account) {
    // A non-404 load failure (network down, 5xx) must not leave a blank page.
    return (
      <PageContainer
        title="Corporate Account"
        breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Corporate Accounts', href: '/corporate-accounts' }, { label: 'Error' }]}
      >
        <EmptyState
          variant="page"
          icon={<BuildingIcon className="h-14 w-14" />}
          title="Could not load this corporate account"
          description={loadError ?? 'Failed to load account.'}
          action={{ label: 'Retry', onClick: () => { void fetchAccount(); } }}
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer
      title={account.company_name}
      subtitle={`Corporate Account${account.gstin ? ` · ${account.gstin}` : ''}`}
      breadcrumbs={[
        { label: 'Home', href: '/home' },
        { label: 'Corporate Accounts', href: '/corporate-accounts' },
        { label: account.company_name },
      ]}
      actions={
        <div className="flex items-center gap-2">
          {canCreateOrder && (
            <Button variant="primary" size="sm" onClick={() => setOrderModalOpen(true)}>
              <PlusIcon /><span className="ml-1.5">New Order</span>
            </Button>
          )}
        </div>
      }
    >
      <div className="animate-fade-in">
        {/* Header Gradient */}
        <div className="bg-gradient-to-r from-indigo-600 to-violet-600 rounded-2xl p-6 text-white shadow-[0_20px_60px_-20px_rgba(99,102,241,0.45)] mb-6 relative overflow-hidden">
          <div className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-20 -left-10 h-48 w-48 rounded-full bg-black/10 blur-3xl" />
          <div className="relative flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Badge variant={account.is_active ? 'success' : 'draft'} size="sm">
                  {account.is_active ? 'Active' : 'Inactive'}
                </Badge>
                {account.gstin && (
                  <span className="text-xs font-mono tracking-wide text-white/70">{account.gstin}</span>
                )}
              </div>
              <h1 className="text-2xl font-bold text-white">{account.company_name}</h1>
              <p className="text-indigo-100 mt-1">{account.contact_name} · {account.contact_phone}</p>
            </div>
          </div>
        </div>

        {/* Stat Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6 overflow-x-auto">
          <div className="animate-slide-up">
            <StatCard
              title="Credit Limit"
              value={formatPaise(account.credit_limit, currency)}
              icon={<CurrencyIcon className="h-4 w-4" />}
            />
          </div>
          <div className="animate-slide-up" style={{ animationDelay: '50ms' }}>
            <StatCard
              title="Outstanding"
              value={formatPaise(account.outstanding_balance, currency)}
              icon={<CurrencyIcon className="h-4 w-4" />}
              anomaly={account.credit_limit > 0 && account.outstanding_balance > account.credit_limit * 0.9}
            />
          </div>
          <div className="animate-slide-up" style={{ animationDelay: '100ms' }}>
            <StatCard
              title="Available Credit"
              value={formatPaise(availableCredit, currency)}
              icon={<CurrencyIcon className="h-4 w-4" />}
            />
          </div>
          <div className="animate-slide-up" style={{ animationDelay: '150ms' }}>
            <StatCard
              title="Total Orders"
              value={(account.total_orders ?? 0).toLocaleString('en-IN')}
              icon={<PackageIcon className="h-4 w-4" />}
            />
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <Tabs items={TABS} activeId={activeTab} onChange={(id) => setActiveTab(id as TabId)} variant="segmented" />
        </div>

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div className="animate-slide-up">
            {orders.length === 0 ? (
              <EmptyState
                variant="page"
                icon={<PackageIcon className="h-14 w-14" />}
                title="No orders yet"
                description="Create a corporate order against this account"
                action={canCreateOrder ? { label: 'New Corporate Order', onClick: () => setOrderModalOpen(true) } : undefined}
              />
            ) : (
              <div className={cn(GLASS, 'overflow-hidden')}>
                <div className="px-5 py-4">
                  <h3 className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider">
                    Orders ({orders.length})
                  </h3>
                </div>
                <DataTable<CorporateOrder> columns={orderColumns} data={orders} />
              </div>
            )}
          </div>
        )}

        {/* Invoices Tab */}
        {activeTab === 'invoices' && (
          <div className="animate-slide-up">
            {invoices.length === 0 ? (
              <EmptyState
                variant="page"
                icon={<BuildingIcon className="h-14 w-14" />}
                title="No invoices"
                description="Invoice a delivered order on the Orders tab to see it here"
              />
            ) : (
              <div className={cn(GLASS, 'overflow-hidden')}>
                <div className="px-5 py-4">
                  <h3 className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider">
                    Invoices ({invoices.length})
                  </h3>
                </div>
                <DataTable<CorporateInvoice> columns={invoiceColumns} data={invoices} />
              </div>
            )}
          </div>
        )}

        {/* Payments Tab */}
        {activeTab === 'payments' && (
          <div className="animate-slide-up">
            {payments.length === 0 ? (
              <EmptyState
                variant="page"
                icon={<CurrencyIcon className="h-14 w-14" />}
                title="No payments yet"
                description="Record a payment against an invoice on the Invoices tab"
              />
            ) : (
              <div className={cn(GLASS, 'overflow-hidden')}>
                <div className="px-5 py-4">
                  <h3 className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider">
                    Payments ({payments.length})
                  </h3>
                </div>
                <DataTable<CorporatePayment> columns={paymentColumns} data={payments} />
              </div>
            )}
          </div>
        )}

        {/* Details Tab */}
        {activeTab === 'details' && (
          <div className={cn(GLASS, 'p-5 animate-slide-up')}>
            <div className="space-y-4 max-w-2xl">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">Company Name</label>
                  <Input
                    value={editCompanyName}
                    onChange={(e) => setEditCompanyName(typeof e === 'string' ? e : '')}
                    disabled={!canEdit}
                  />
                </div>
                {/* Statutory tax id — `GSTIN` (India) / `TRN`·`VATIN`·… (GCC).
                    Qatar/Kuwait have no statutory id ⇒ field hidden. */}
                {tax.taxIdLabel && (
                  <div>
                    <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">{tax.taxIdLabel}</label>
                    <Input
                      value={editGstin}
                      onChange={(e) => setEditGstin(typeof e === 'string' ? e.toUpperCase() : '')}
                      disabled={!canEdit}
                      className="font-mono tracking-wide"
                    />
                  </div>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">Contact Person</label>
                  <Input
                    value={editContactName}
                    onChange={(e) => setEditContactName(typeof e === 'string' ? e : '')}
                    disabled={!canEdit}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">Contact Phone</label>
                  <Input
                    variant="phone"
                    defaultDial={dial}
                    value={editContactPhone}
                    onChange={(e) => setEditContactPhone(typeof e === 'string' ? e : '')}
                    disabled={!canEdit}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">Contact Email</label>
                  <Input
                    value={editContactEmail}
                    onChange={(e) => setEditContactEmail(typeof e === 'string' ? e : '')}
                    disabled={!canEdit}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">Credit Limit ({getCurrencySymbol(currency)}) *</label>
                  <Input
                    type="number"
                    value={editCreditLimitRupees}
                    onChange={(e) => setEditCreditLimitRupees(typeof e === 'string' ? e : '')}
                    placeholder="e.g. 50000 · 0 = No credit"
                    disabled={!canEdit}
                  />
                  <p className="text-[10px] text-neutral-400 dark:text-neutral-500 mt-1">Required. Enter 0 for no credit.</p>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">Billing Address</label>
                <TextArea
                  value={editBillingAddress}
                  onChange={(val) => setEditBillingAddress(val)}
                  disabled={!canEdit}
                  rows={3}
                />
              </div>
              <div className="flex items-center justify-between pt-4">
                <div>
                  {canDeactivate && account.is_active && (
                    <Button variant="danger" size="sm" onClick={handleDeactivate}>
                      Deactivate Account
                    </Button>
                  )}
                </div>
                {canEdit && (
                  <Button variant="primary" size="sm" onClick={handleSave} disabled={isSaving}>
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Order step confirm (in-app, never a native confirm()) */}
      <Modal
        open={pendingAction !== null}
        onClose={() => { if (!isActing) setPendingAction(null); }}
        title={pendingAction ? ACTION_COPY[pendingAction.action].title : ''}
      >
        {pendingAction && (
          <div className="space-y-4 p-1">
            <p className="text-sm text-neutral-700 dark:text-neutral-200">{ACTION_COPY[pendingAction.action].body}</p>
            <div className={cn(GLASS_FILTER, 'p-3 flex items-center justify-between')}>
              <span className="text-xs font-mono tracking-wide text-neutral-500 dark:text-neutral-400">{pendingAction.order.order_number}</span>
              <span className="text-sm font-bold font-mono tabular-nums text-neutral-900 dark:text-neutral-100">{formatPaise(pendingAction.order.total, currency)}</span>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="secondary" size="sm" onClick={() => setPendingAction(null)} disabled={isActing}>
                Back
              </Button>
              <Button
                variant={pendingAction.action === 'cancel' ? 'danger' : 'primary'}
                size="sm"
                onClick={handleOrderAction}
                disabled={isActing}
              >
                {isActing ? 'Working...' : pendingAction.action === 'cancel' ? 'Cancel order' : ACTION_COPY[pendingAction.action].label}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Record Payment Modal */}
      <Modal
        open={paymentInvoice !== null}
        onClose={() => { if (!isRecordingPayment) setPaymentInvoice(null); }}
        title="Record payment"
      >
        {paymentInvoice && (
          <div className="space-y-4 p-1">
            <div className={cn(GLASS_FILTER, 'p-3 flex items-center justify-between')}>
              <span className="text-xs font-mono tracking-wide text-neutral-500 dark:text-neutral-400">{paymentInvoice.invoice_number}</span>
              <span className="text-sm text-neutral-700 dark:text-neutral-200">
                Left to pay <span className="font-bold font-mono tabular-nums">{formatPaise(paymentInvoice.balance, currency)}</span>
              </span>
            </div>
            <Input
              label={`Amount (${getCurrencySymbol(currency)})`}
              type="number"
              value={paymentAmount}
              onChange={(v) => setPaymentAmount(typeof v === 'string' ? v : '')}
            />
            <div>
              <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">Method</label>
              <Select
                options={PAYMENT_METHOD_OPTIONS}
                value={paymentMethod}
                onChange={(val) => setPaymentMethod((typeof val === 'string' ? val : val[0]) as CorporatePaymentMethod)}
              />
            </div>
            <Input
              label="Reference (optional)"
              value={paymentReference}
              onChange={(v) => setPaymentReference(typeof v === 'string' ? v : '')}
              placeholder="UTR / cheque / card slip number"
            />
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="secondary" size="sm" onClick={() => setPaymentInvoice(null)} disabled={isRecordingPayment}>
                Cancel
              </Button>
              <Button variant="primary" size="sm" onClick={handleRecordPayment} disabled={isRecordingPayment || !paymentAmount}>
                {isRecordingPayment ? 'Saving...' : 'Record payment'}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Create Order Modal */}
      <Modal open={orderModalOpen} onClose={closeOrderModal} title="New Corporate Order" variant="wide">
        <div className="space-y-4 p-1 max-h-[70vh] overflow-y-auto">
          {needsOwnerApproval ? (
            <div className="space-y-3 rounded-xl bg-warning-50 dark:bg-warning-900/20 p-4 animate-fade-in">
              <p className="text-sm font-semibold text-warning-800 dark:text-warning-200">Needs owner approval</p>
              <p className="text-sm text-neutral-700 dark:text-neutral-200">
                {approvalMessage ??
                  `The order total (${formatPaise(orderTotal, currency)}) is over the available credit (${formatPaise(availableCredit, currency)}). An owner must approve it.`}
              </p>
              {approvers.length === 0 ? (
                <p className="text-sm text-neutral-700 dark:text-neutral-200">
                  No owner has a POS PIN yet. An owner can set one on the Users page, or place this order themselves.
                </p>
              ) : (
                <>
                  <RadioGroup
                    label="Owner approving"
                    options={approvers.map((a) => ({ value: a.id, label: `${a.name} (Owner)` }))}
                    value={approverId}
                    onChange={(v) => setApproverId(v)}
                  />
                  <Input
                    label="Owner PIN"
                    type="password"
                    inputMode="numeric"
                    value={approverPin}
                    onChange={(v) => setApproverPin(v.replace(/\D/g, '').slice(0, 6))}
                  />
                </>
              )}
            </div>
          ) : creditExceeded && (
            <Alert
              variant="warning"
              title="Over the credit limit"
              description={`Order total exceeds available credit (${formatPaise(availableCredit, currency)}). As the owner, you can still place it.`}
            />
          )}

          <div>
            <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">Location</label>
            <Select
              options={locationOptions}
              value={orderLocationId}
              onChange={(val) => {
                const loc = typeof val === 'string' ? val : val[0] ?? '';
                setOrderLocationId(loc);
                // Products differ per location — clear any picked items so the
                // order can't carry products that don't exist at the new location.
                setOrderItems([]);
              }}
              placeholder="Select a location"
            />
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400">Order Items</label>
              <button
                type="button"
                onClick={() => setOrderItems([...orderItems, { product_id: '', product_name: '', quantity: 1, unit_price: 0 }])}
                className="text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 transition-colors duration-200"
              >
                + Add Item
              </button>
            </div>
            {orderItems.map((item, idx) => (
              <div key={idx} className={cn(GLASS_FILTER, 'p-3 flex items-center gap-3')}>
                <div className="flex-1">
                  <Select
                    variant="searchable"
                    options={productOptions}
                    value={item.product_id}
                    onChange={(val) => {
                      const pid = typeof val === 'string' ? val : val[0] ?? '';
                      const prod = productMap[pid];
                      const next = [...orderItems];
                      next[idx] = {
                        ...next[idx],
                        product_id: pid,
                        product_name: prod?.name ?? '',
                        unit_price: prod?.price ?? 0,
                      };
                      setOrderItems(next);
                    }}
                    placeholder="Search and select a product"
                  />
                </div>
                <div className="w-20">
                  <Input
                    type="number"
                    value={String(item.quantity)}
                    onChange={(v) => {
                      const next = [...orderItems];
                      next[idx] = { ...next[idx], quantity: Math.max(1, parseInt(v, 10) || 1) };
                      setOrderItems(next);
                    }}
                    placeholder="Qty"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setOrderItems(orderItems.filter((_, i) => i !== idx))}
                  className="p-2 text-neutral-400 hover:text-danger-500 transition-colors duration-200"
                  aria-label="Remove item"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            ))}
            {orderItems.length === 0 && (
              <p className="text-sm text-neutral-400 dark:text-neutral-500 text-center py-4">No items added. Click "+ Add Item" to begin.</p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">Delivery Date</label>
              <DatePicker
                value={orderDeliveryDate ? new Date(orderDeliveryDate + 'T00:00:00') : undefined}
                onChange={(d: Date) => setOrderDeliveryDate(toLocalIsoDate(d))}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">Order Total (incl. tax)</label>
              <p className="text-lg font-bold font-mono tabular-nums text-neutral-900 dark:text-neutral-100 mt-1">
                {formatPaise(orderTotal, currency)}
              </p>
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">Notes</label>
            <TextArea
              value={orderNotes}
              onChange={(val) => setOrderNotes(val)}
              placeholder="Delivery instructions, special requirements..."
              rows={2}
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" size="sm" onClick={closeOrderModal} disabled={isCreatingOrder}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleCreateOrder}
              disabled={
                isCreatingOrder ||
                orderItems.length === 0 ||
                (needsOwnerApproval && (!approverId || !/^\d{4,6}$/.test(approverPin)))
              }
            >
              {isCreatingOrder ? 'Creating...' : needsOwnerApproval ? 'Approve and Create Order' : 'Create Order'}
            </Button>
          </div>
        </div>
      </Modal>
    </PageContainer>
  );
}

export function CorporateAccountDetailPage() {
  return (
    <ErrorBoundary>
      <CorporateAccountDetailContent />
    </ErrorBoundary>
  );
}
